import React, { createContext, useState, useEffect } from 'react';
import { authApi } from '../services/authApi';
import { friendshipApi } from '../services/friendshipApi';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

  const loadPendingRequests = async () => {
    try {
      const response = await friendshipApi.getFriendships();
      if (response.success) {
        const pending = response.data?.filter(f => f.status === 'pending' && f.addressee_id === user?.id) || [];
        setPendingRequestsCount(pending.length);
      }
    } catch (error) {
      console.error('Failed to load pending requests:', error);
    }
  };

  useEffect(() => {
    // Check if user is logged in on mount
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
      // Optionally verify token with backend
      authApi.getMe()
        .then(response => {
          if (response.success) {
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
          }
        })
        .catch(() => {
          // Token invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  // Load pending friend requests when user changes
  useEffect(() => {
    if (user) {
      loadPendingRequests();
    } else {
      setPendingRequestsCount(0);
    }
  }, [user]);

  const login = async (credentials) => {
    const response = await authApi.login(credentials);
    if (response.success) {
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      return response;
    }
    throw new Error(response.error || 'Login failed');
  };

  const register = async (data) => {
    const response = await authApi.register(data);
    if (response.success) {
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      return response;
    }
    throw new Error(response.error || 'Registration failed');
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        updateUser,
        isAuthenticated: !!user,
        pendingRequestsCount,
        refreshPendingRequests: loadPendingRequests,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
